/**
 * Frontend Print Utility
 * Handles all print operations with multi-language and dentist profile support
 */

import { message } from 'antd';
import i18n from '../i18n';

/**
 * Get text direction for RTL/LTR
 * @param {string} locale
 * @returns {string}
 */
export const getTextDirection = (locale = i18n.language) => {
    return ['ar', 'ku'].includes(locale) ? 'rtl' : 'ltr';
};

/**
 * Format currency based on locale
 * @param {number} amount
 * @param {string} currency
 * @param {string} locale
 * @returns {string}
 */
export const formatCurrency = (amount, currency = 'USD', locale = i18n.language) => {
    const num = Number(amount) || 0;

    if (currency === 'IQD') {
        return `${num.toLocaleString(locale === 'ar' ? 'ar-IQ' : 'en-US')} IQD`;
    }

    return num.toLocaleString(locale === 'ar' ? 'ar-IQ' : 'en-US', {
        style: 'currency',
        currency: 'USD'
    });
};

/**
 * Format date based on locale
 * @param {Date|string} date
 * @param {string} locale
 * @returns {string}
 */
export const formatDate = (date, locale = i18n.language) => {
    if (!date) return '';

    const dateObj = new Date(date);

    if (locale === 'ar') {
        return dateObj.toLocaleDateString('ar-IQ');
    } else if (locale === 'ku') {
        return dateObj.toLocaleDateString('en-US');
    }

    return dateObj.toLocaleDateString('en-US');
};

/**
 * Get common print CSS styles
 * @param {string} locale
 * @returns {string}
 */
const getCommonPrintStyles = (locale = i18n.language) => {
    const direction = getTextDirection(locale);

    return `
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        html {
            direction: ${direction};
            lang: ${locale};
        }
        
        body {
            font-family: ${direction === 'rtl' ? "'Segoe UI', 'Arial Unicode MS'" : "'Segoe UI', Arial"}, sans-serif;
            padding: 30px;
            color: #333;
            max-width: 900px;
            margin: 0 auto;
            line-height: 1.6;
            background: white;
        }
        
        .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #5f7868;
            flex-direction: ${direction === 'rtl' ? 'row-reverse' : 'row'};
        }
        
        .clinic-info h1 {
            color: #5f7868;
            font-size: 24px;
            margin-bottom: 10px;
            font-weight: 600;
        }
        
        .clinic-info p {
            font-size: 13px;
            color: #666;
            line-height: 1.6;
            margin-bottom: 5px;
        }
        
        .print-title {
            text-align: ${direction === 'rtl' ? 'left' : 'right'};
        }
        
        .print-title h2 {
            color: #5f7868;
            font-size: 28px;
            margin-bottom: 5px;
            font-weight: 600;
        }
        
        .print-title p {
            font-size: 13px;
            color: #666;
            margin: 3px 0;
        }
        
        .info-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 25px;
            gap: 20px;
            flex-direction: ${direction === 'rtl' ? 'row-reverse' : 'row'};
        }
        
        .info-box {
            flex: 1;
            text-align: ${direction === 'rtl' ? 'right' : 'left'};
        }
        
        .info-box h3 {
            font-size: 12px;
            text-transform: uppercase;
            color: #5f7868;
            margin-bottom: 10px;
            letter-spacing: 1px;
            font-weight: 600;
            border-bottom: 1px solid #e0e0e0;
            padding-bottom: 5px;
        }
        
        .info-box p {
            font-size: 14px;
            line-height: 1.8;
            margin-bottom: 3px;
        }
        
        .info-box strong {
            font-weight: 600;
            color: #333;
        }
        
        table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            background: white;
        }
        
        th {
            background-color: #5f7868;
            color: white;
            padding: 12px;
            text-align: ${direction === 'rtl' ? 'right' : 'left'};
            font-size: 13px;
            text-transform: uppercase;
            font-weight: 600;
            letter-spacing: 0.5px;
        }
        
        td {
            border-bottom: 1px solid #eee;
            padding: 12px;
            font-size: 14px;
            text-align: ${direction === 'rtl' ? 'right' : 'left'};
        }
        
        tr:nth-child(even) {
            background-color: #f9f9f9;
        }
        
        .totals-section {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 30px;
            flex-direction: ${direction === 'rtl' ? 'row-reverse' : 'row'};
        }
        
        .totals-table {
            width: 350px;
        }
        
        .totals-table tr td {
            border: none;
            padding: 8px 12px;
            border-bottom: 1px solid #eee;
        }
        
        .totals-table tr:last-child td {
            border-bottom: 2px solid #5f7868;
            border-top: 2px solid #5f7868;
            font-weight: 600;
            font-size: 15px;
            padding: 12px;
        }
        
        .totals-label {
            font-weight: 500;
            color: #666;
        }
        
        .totals-amount {
            text-align: ${direction === 'rtl' ? 'left' : 'right'};
            font-weight: 600;
            color: #333;
        }
        
        .dentist-info {
            margin: 30px 0;
            padding: 20px;
            background: #f9f9f9;
            border-radius: 5px;
            border-left: 4px solid #5f7868;
        }
        
        .dentist-info h3 {
            font-size: 13px;
            text-transform: uppercase;
            color: #5f7868;
            margin-bottom: 10px;
            letter-spacing: 1px;
            font-weight: 600;
        }
        
        .status-badge {
            display: inline-block;
            padding: 5px 12px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
        }
        
        .status-paid { background: #e6f7e6; color: #2e7d32; }
        .status-pending { background: #fff3e0; color: #e65100; }
        .status-partial { background: #e3f2fd; color: #1565c0; }
        
        .print-footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 12px;
            color: #999;
        }
        
        .signature-section {
            margin-top: 30px;
            display: flex;
            justify-content: ${direction === 'rtl' ? 'flex-start' : 'flex-end'};
            gap: 40px;
        }
        
        .signature-box {
            width: 200px;
            text-align: center;
        }
        
        .signature-line {
            border-bottom: 1px solid #333;
            margin-bottom: 5px;
            height: 40px;
        }
        
        .signature-label {
            font-size: 13px;
            color: #666;
            font-weight: 600;
        }
        
        @media print {
            body { padding: 15px; max-width: 100%; }
            .no-print { display: none !important; }
            .print-header { page-break-inside: avoid; }
            .info-section { page-break-inside: avoid; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; }
            .print-footer { page-break-inside: avoid; }
        }
    `;
};

/**
 * Open print window with HTML content
 * @param {string} htmlContent
 * @param {string} title
 */
const openPrintWindow = (htmlContent, title = 'Document') => {
    try {
        const printWindow = window.open('', '_blank');

        if (!printWindow) {
            message.error(i18n.t('common:printBlocked') || 'Please allow popups to print');
            return { success: false };
        }

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();

        // Delay to ensure content is loaded
        setTimeout(() => {
            printWindow.print();
        }, 300);

        return { success: true };
    } catch (error) {
        console.error('Print error:', error);
        message.error(i18n.t('common:printError') || 'Failed to print document');
        return { success: false };
    }
};

/**
 * Print Invoice from API
 * @param {string} invoiceId
 * @param {Object} options
 */
export const printInvoice = async (invoiceId, options = {}) => {
    const { api } = options;
    const locale = i18n.language;

    if (!api) {
        message.error('API not available');
        return;
    }

    try {
        const response = await api.get(`/payments/${invoiceId}/print?lang=${locale}`);

        if (response.data.success) {
            const data = response.data.data;
            const direction = getTextDirection(locale);

            const htmlContent = `
                <!DOCTYPE html>
                <html dir="${direction}">
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <title>${i18n.t('payment:invoice') || 'Invoice'}</title>
                    <style>
                        ${getCommonPrintStyles(locale)}
                    </style>
                </head>
                <body>
                    ${data.html}
                </body>
                </html>
            `;

            openPrintWindow(htmlContent, 'Invoice');
        }
    } catch (error) {
        console.error('Invoice print error:', error);
        message.error(i18n.t('common:printError') || 'Failed to load invoice for printing');
    }
};

/**
 * Print Prescription
 * @param {Object} prescription
 * @param {Object} patient
 * @param {Object} dentist
 * @param {Object} clinic
 */
export const printPrescription = (prescription, patient, dentist, clinic) => {
    const locale = i18n.language;
    const direction = getTextDirection(locale);

    const htmlContent = `
        <!DOCTYPE html>
        <html dir="${direction}">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${i18n.t('prescription:title') || 'Prescription'}</title>
            <style>
                ${getCommonPrintStyles(locale)}
            </style>
        </head>
        <body>
            <div class="print-header">
                <div class="clinic-info">
                    <h1>${clinic?.name || 'Dental Clinic'}</h1>
                    ${clinic?.address ? `<p>${clinic.address}</p>` : ''}
                    ${clinic?.phone ? `<p><strong>Phone:</strong> ${clinic.phone}</p>` : ''}
                </div>
                <div class="print-title">
                    <h2>🦷 ${i18n.t('prescription:title') || 'PRESCRIPTION'}</h2>
                    <p><strong>Date:</strong> ${formatDate(prescription.date, locale)}</p>
                </div>
            </div>

            <div class="info-section">
                <div class="info-box">
                    <h3>${locale === 'ar' ? 'معلومات المريض' : locale === 'ku' ? 'زانیاری نەخۆش' : 'PATIENT INFORMATION'}</h3>
                    <p><strong>${patient?.name || 'N/A'}</strong></p>
                    ${patient?.age ? `<p><strong>${i18n.t('patient:age') || 'Age'}:</strong> ${patient.age}</p>` : ''}
                    ${patient?.contact ? `<p><strong>Phone:</strong> ${patient.contact}</p>` : ''}
                </div>
                <div class="info-box">
                    <h3>${locale === 'ar' ? 'طبيب الأسنان' : locale === 'ku' ? 'دندانپزشک' : 'DENTIST'}</h3>
                    <p><strong>${dentist?.name || 'N/A'}</strong></p>
                    ${dentist?.specialization ? `<p>${dentist.specialization}</p>` : ''}
                    ${dentist?.phone ? `<p><strong>Phone:</strong> ${dentist.phone}</p>` : ''}
                </div>
            </div>

            ${prescription?.selectedTeeth && prescription.selectedTeeth.length > 0 ? `
            <div style="margin: 20px 0; padding: 15px; background: #f9f9f9; border-radius: 5px;">
                <h3 style="margin-bottom: 10px; color: #5f7868;">${locale === 'ar' ? 'الأسنان المعالجة' : locale === 'ku' ? 'دندانە چاودێری کراو' : 'Teeth Treated'}</h3>
                <div style="display: flex; flex-wrap: wrap; gap: 5px;">
                    ${prescription.selectedTeeth.map(tooth => `
                        <span style="display: inline-block; padding: 5px 10px; background: #5f7868; color: white; border-radius: 3px; font-size: 12px; font-weight: bold;">
                            ${tooth.toothNumber || tooth}
                        </span>
                    `).join('')}
                </div>
            </div>
            ` : ''}

            ${prescription?.medications && prescription.medications.length > 0 ? `
            <div style="margin: 20px 0;">
                <h3 style="color: #5f7868; margin-bottom: 15px; font-size: 14px; text-transform: uppercase;">
                    ${locale === 'ar' ? 'الأدوية' : locale === 'ku' ? 'دەرمان' : 'MEDICATIONS'}
                </h3>
                <table>
                    <thead>
                        <tr>
                            <th>${locale === 'ar' ? 'الدواء' : locale === 'ku' ? 'دەرمان' : 'Medicine'}</th>
                            <th>${locale === 'ar' ? 'الجرعة' : locale === 'ku' ? 'بەرھەم' : 'Dosage'}</th>
                            <th>${locale === 'ar' ? 'التكرار' : locale === 'ku' ? 'جار' : 'Frequency'}</th>
                            <th>${locale === 'ar' ? 'المدة' : locale === 'ku' ? 'مەودا' : 'Duration'}</th>
                            <th>${locale === 'ar' ? 'التعليمات' : locale === 'ku' ? 'ڕێنیونی' : 'Instructions'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${prescription.medications.map(med => `
                            <tr>
                                <td>${med.name || ''}</td>
                                <td>${med.dosage || '-'}</td>
                                <td>${med.frequency || '-'}</td>
                                <td>${med.duration || '-'}</td>
                                <td>${med.instructions || '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
            ` : ''}

            ${prescription?.generalInstructions ? `
            <div style="margin: 20px 0; padding: 15px; background: #f9f9f9; border-radius: 5px;">
                <h3 style="color: #5f7868; margin-bottom: 10px; font-size: 14px; text-transform: uppercase;">
                    ${locale === 'ar' ? 'التعليمات العامة' : locale === 'ku' ? 'ڕێنیونی گشتی' : 'GENERAL INSTRUCTIONS'}
                </h3>
                <p>${prescription.generalInstructions}</p>
            </div>
            ` : ''}

            <div class="signature-section">
                <div class="signature-box">
                    <div class="signature-line"></div>
                    <div class="signature-label">
                        <strong>Dr. ${dentist?.name || 'Dentist'}</strong><br>
                        ${dentist?.specialization || ''}
                    </div>
                </div>
            </div>

            <div class="print-footer">
                <p>${formatDate(new Date(), locale)}</p>
            </div>
        </body>
        </html>
    `;

    openPrintWindow(htmlContent, 'Prescription');
};

/**
 * Print Dental Chart
 * @param {Object} chartData
 * @param {Object} patient
 * @param {Object} dentist
 */
export const printDentalChart = (chartData, patient, dentist) => {
    const locale = i18n.language;
    const direction = getTextDirection(locale);

    const htmlContent = `
        <!DOCTYPE html>
        <html dir="${direction}">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${i18n.t('tooth:chart') || 'Dental Chart'}</title>
            <style>
                ${getCommonPrintStyles(locale)}
                
                .dental-chart {
                    margin: 20px 0;
                    padding: 20px;
                    background: white;
                    border-radius: 5px;
                }
                
                .teeth-grid {
                    display: grid;
                    grid-template-columns: repeat(8, 1fr);
                    gap: 10px;
                    margin: 20px 0;
                }
                
                .tooth-box {
                    border: 2px solid #5f7868;
                    border-radius: 5px;
                    padding: 15px;
                    text-align: center;
                    background: white;
                    min-height: 100px;
                }
                
                .tooth-number {
                    font-weight: bold;
                    color: #5f7868;
                    font-size: 16px;
                    margin-bottom: 10px;
                }
                
                .tooth-condition {
                    font-size: 12px;
                    color: #666;
                    line-height: 1.5;
                }
            </style>
        </head>
        <body>
            <div class="print-header">
                <div class="clinic-info">
                    <h1>${i18n.t('patient:dentalChart') || 'Dental Chart'}</h1>
                </div>
                <div class="print-title">
                    <h2>🦷 ${i18n.t('patient:dentalChart') || 'DENTAL CHART'}</h2>
                    <p><strong>Date:</strong> ${formatDate(new Date(), locale)}</p>
                </div>
            </div>

            <div class="info-section">
                <div class="info-box">
                    <h3>${locale === 'ar' ? 'بيانات المريض' : locale === 'ku' ? 'زانیاری نەخۆش' : 'PATIENT'}</h3>
                    <p><strong>${patient?.name || 'N/A'}</strong></p>
                    ${patient?.contact ? `<p><strong>Phone:</strong> ${patient.contact}</p>` : ''}
                </div>
                <div class="info-box">
                    <h3>${locale === 'ar' ? 'طبيب الأسنان' : locale === 'ku' ? 'دندانپزشک' : 'DENTIST'}</h3>
                    <p><strong>${dentist?.name || 'N/A'}</strong></p>
                </div>
            </div>

            <div class="dental-chart">
                ${chartData ? `<pre>${JSON.stringify(chartData, null, 2)}</pre>` : '<p>No dental chart data</p>'}
            </div>

            <div class="print-footer">
                <p>${formatDate(new Date(), locale)}</p>
            </div>
        </body>
        </html>
    `;

    openPrintWindow(htmlContent, 'Dental Chart');
};

export default {
    printInvoice,
    printPrescription,
    printDentalChart,
    formatCurrency,
    formatDate,
    getTextDirection
};
